import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Menu,
  MenuButton,
  MenuList,
  MenuItemOption,
  MenuOptionGroup,
  Button,
  Icon,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { FaSearch, FaFilter, FaTrash } from 'react-icons/fa';

import { useColors } from './colors';

import { getSuppliersListApi } from 'features/suppliers/suppliers.api';
import { deleteInventoriesByFilterApi, getInventoriesApi } from 'features/inventories/inventories.api';

const FiltersSection = ({
  searchValue,
  selectedSupplier,
  selectedStatuses,
  onSearch,
  onSupplierChange,
  onStatusChange,
  onSearchSubmit,
}) => {
  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { list: supplierOptions, getSuppliersListStatus } = useSelector(
    (state) => state.suppliers,
  );
  const { deleteInventoriesByFilterStatus } = useSelector((state) => state.inventories);

  const [deleteForm, setDeleteForm] = useState({
    startDate: '',
    endDate: '',
  });

  const {
    textColor,
    backgroundCardColor,
    inputBackground,
    hoverBackground,
    menuBackground,
    borderColor,
  } = useColors();

  useEffect(() => {
    dispatch(getSuppliersListApi());
  }, [dispatch]);

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      onSearchSubmit(e.target.value);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    onSearch(value);
    if (value === '') {
      onSearchSubmit('');
    }
  };

  const handleDeleteFormChange = (field, value) => {
    setDeleteForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBulkDelete = async () => {
    if (!deleteForm.startDate || !deleteForm.endDate) {
      return;
    }

    try {
      const result = await dispatch(deleteInventoriesByFilterApi({
        startDate: deleteForm.startDate,
        endDate: deleteForm.endDate,
      })).unwrap();
      
      if (result.success) {
        // Refresh the inventories list after successful deletion
        dispatch(getInventoriesApi({ page: 1, limit: 10 }));
        onClose();
        setDeleteForm({ startDate: '', endDate: '' });
      }
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const isDeleteFormValid = deleteForm.startDate && deleteForm.endDate && 
    new Date(deleteForm.startDate) <= new Date(deleteForm.endDate);

  return (
    <>
      <Box bg={backgroundCardColor} borderRadius="lg" boxShadow="sm" p={6} mb={4}>
        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={4}
          align={{ base: 'stretch', md: 'center' }}
        >
          {/* Search Bar */}
          <InputGroup maxW={{ base: '100%', md: '400px' }}>
            <InputLeftElement pointerEvents="none" h="full" pl={4}>
              <Icon as={FaSearch} color="gray.400" boxSize={5} />
            </InputLeftElement>
            <Input
              value={searchValue}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
              placeholder="Search by file name..."
              color={textColor}
              bg={inputBackground}
              _placeholder={{ color: 'gray.400' }}
              borderRadius="lg"
              size="lg"
              pl={12}
            />
          </InputGroup>

          <Flex
            gap={4}
            flex={1}
            justify="flex-end"
            direction={{ base: 'column', md: 'row' }}
          >
            {/* Supplier Filter */}
            <Select
              value={selectedSupplier}
              onChange={(e) => onSupplierChange(e.target.value)}
              isDisabled={getSuppliersListStatus === 'pending'}
              maxW={{ base: '100%', md: '250px' }}
              placeholder="Select Supplier"
              color={textColor}
              bg={inputBackground}
              borderRadius="lg"
              size="lg"
              icon={<FaFilter />}
              iconSize={20}
              sx={{
                option: {
                  bg: menuBackground,
                  color: textColor,
                },
                '> option:hover': {
                  bg: useColorModeValue('gray.100', 'navy.700') + '!important',
                },
                '> option:checked': {
                  bg: useColorModeValue('blue.50', 'navy.900') + '!important',
                },
              }}
              _hover={{
                bg: hoverBackground,
              }}
              _focus={{
                bg: inputBackground,
                borderColor: 'blue.300',
              }}
            >
              <option value="all">All Suppliers</option>
              {supplierOptions.map((option) => (
                <option key={option.id} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>

            {/* Status Filters */}
            <Menu closeOnSelect={false}>
              <MenuButton
                as={Button}
                rightIcon={<FaFilter />}
                bg={inputBackground}
                color={textColor}
                borderColor={borderColor}
                variant="outline"
                w={{ base: '100%', md: 'auto' }}
                size="lg"
                borderRadius="lg"
                minW="200px"
                _hover={{
                  bg: hoverBackground,
                }}
                _active={{
                  bg: hoverBackground,
                }}
                _focus={{
                  bg: inputBackground,
                  borderColor: 'blue.300',
                }}
              >
                Status Filters
              </MenuButton>
              <MenuList
                bg={menuBackground}
                borderColor="gray.600"
                p={3}
                minW="220px"
                shadow="lg"
              >
                <MenuOptionGroup
                  title="Processing Status"
                  type="checkbox"
                  value={selectedStatuses.filter((s) =>
                    ['chunked', 'not-chunked'].includes(s),
                  )}
                  onChange={(values) =>
                    onStatusChange([
                      ...selectedStatuses.filter(
                        (s) => !['chunked', 'not-chunked'].includes(s),
                      ),
                      ...values,
                    ])
                  }
                  color={textColor}
                  fontSize="sm"
                >
                  <MenuItemOption value="chunked" color={textColor} bg="transparent">
                    Chunked
                  </MenuItemOption>
                  <MenuItemOption value="not-chunked" color={textColor} bg="transparent">
                    Not Chunked
                  </MenuItemOption>
                </MenuOptionGroup>
                <MenuOptionGroup
                  title="Sync Status"
                  type="checkbox"
                  mt={4}
                  value={selectedStatuses.filter((s) =>
                    ['processed', 'not-processed'].includes(s),
                  )}
                  onChange={(values) =>
                    onStatusChange([
                      ...selectedStatuses.filter(
                        (s) => !['processed', 'not-processed'].includes(s),
                      ),
                      ...values,
                    ])
                  }
                  color={textColor}
                  fontSize="sm"
                >
                  <MenuItemOption value="processed" color={textColor} bg="transparent">
                    Synced
                  </MenuItemOption>
                  <MenuItemOption value="not-processed" color={textColor} bg="transparent">
                    Not Synced
                  </MenuItemOption>
                </MenuOptionGroup>
              </MenuList>
            </Menu>

            {/* Bulk Delete Button */}
            <Button
              leftIcon={<FaTrash />}
              colorScheme="red"
              variant="outline"
              size="lg"
              borderRadius="lg"
              onClick={onOpen}
              _hover={{
                bg: 'red.50',
                borderColor: 'red.300',
              }}
              _dark={{
                _hover: {
                  bg: 'red.900',
                  borderColor: 'red.300',
                },
              }}
            >
              Bulk Delete
            </Button>
          </Flex>
        </Flex>
      </Box>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent bg={backgroundCardColor}>
          <ModalHeader color={textColor}>Delete Inventories by Date Range</ModalHeader>
          <ModalCloseButton color={textColor} />
          <ModalBody>
            <Alert status="warning" mb={4} borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                This will permanently delete all inventories and their physical files created within the selected date range.
                Only chunked inventories will be deleted.
              </Text>
            </Alert>

            <FormControl mb={4}>
              <FormLabel color={textColor}>Start Date</FormLabel>
              <Input
                type="date"
                value={deleteForm.startDate}
                onChange={(e) => handleDeleteFormChange('startDate', e.target.value)}
                color={textColor}
                bg={inputBackground}
                borderRadius="md"
              />
            </FormControl>

            <FormControl mb={4}>
              <FormLabel color={textColor}>End Date</FormLabel>
              <Input
                type="date"
                value={deleteForm.endDate}
                onChange={(e) => handleDeleteFormChange('endDate', e.target.value)}
                color={textColor}
                bg={inputBackground}
                borderRadius="md"
              />
            </FormControl>

            {deleteForm.startDate && deleteForm.endDate && new Date(deleteForm.startDate) > new Date(deleteForm.endDate) && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">Start date cannot be after end date.</Text>
              </Alert>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} color={textColor}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleBulkDelete}
              isDisabled={!isDeleteFormValid}
              isLoading={deleteInventoriesByFilterStatus === 'pending'}
              loadingText="Deleting..."
            >
              Delete Inventories
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default FiltersSection;
